import { Loader, SmartListView } from '@/components';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useConversationFetch } from '../../viewmodels/useConversationFetch';
import './ConversationScreen.scss';
import { MessageBubble, StickyChatLabel } from './MessageBubble';
import ScrollToBottomButton from './Scrolltobottombutton';
import { scrollRef } from './conversationRefs';

// ─── LoaderWrapper ─────────────────────────────────────────────────────────

const LoaderWrapper = ({ className }) => (
   <div className={`conversation-list-loader ct-flexCenter ${className}`}>
      <Loader size="lg" variant="primary" />
   </div>
);

LoaderWrapper.propTypes = {
   className: PropTypes.string,
};

function ConversationList() {
   const { activeChatJid } = useSelector(state => state.navigationData.activeRightStack);

   const {
      messages,
      loadOlderMessages,
      loadNewerMessages,
      hasMoreOlder,
      hasMoreNewer,
      loadingTop,
      loadingBottom,
      activeChat,
   } = useConversationFetch(activeChatJid);

   // ─── Container width (for media calculation) ─────────────────────────────

   const containerWidthRef = useRef(300);

   useEffect(() => {
      const el = scrollRef.current?.getElement?.();

      if (!el) return;

      const update = () => {
         containerWidthRef.current = el.clientWidth || 300;
      };

      update();

      const observer = new ResizeObserver(update);
      observer.observe(el);

      return () => observer.disconnect();
   }, []);

   // ─── Smart Item Height Estimation ────────────────────────────────────────

   const getItemHeight = useCallback(item => {
      if (item.type === 'date-label' || item.type === 'unread-separator') {
         return 50;
      }

      // 🖼 Grouped media — 3-column grid layout
      if (item.type === 'groupedMedia') {
         const count = item.messages?.length || 1;
         const containerWidth = containerWidthRef.current || 300;
         const cellSize = (containerWidth - 8) / 3; // 3 cols with ~4px gaps
         const rows = Math.ceil(count / 3);
         return rows * cellSize + rows * 4 + 16; // rows × cell + row gaps + padding
      }

      const { msgType, msgBody = {} } = item;
      const { media = {}, replyTo, message = '' } = msgBody;

      // 📸 Image / Video (dynamic based on aspect ratio)
      if (msgType === 'image' || msgType === 'video') {
         const { webWidth, webHeight, caption = '' } = media;

         const containerWidth = containerWidthRef.current || 300;

         if (webWidth && webHeight) {
            const ratio = webHeight / webWidth;

            const mediaHeight = Math.min(
               containerWidth * ratio,
               500, // cap height
            );

            const captionLines = Math.ceil(caption.length / 30);
            const captionHeight = captionLines * 18;

            return mediaHeight + captionHeight + 40;
         }

         return 300; // fallback
      }

      // 📎 Other media
      if (['file', 'audio', 'location', 'contact'].includes(msgType)) {
         return 280;
      }

      // 💬 Text message
      const base = replyTo ? 120 : 80;
      const lines = Math.ceil(message.length / 30);

      return base + lines * 18;
   }, []);

   // ─── Initial scroll logic ────────────────────────────────────────────────

   const initialScrollKey = useMemo(() => {
      return (activeChat?.unreadCount || 0) > 0 ? 'unread-separator' : null;
   }, [activeChat?.unreadCount]);

   // ─── renderItem ─────────────────────────────────────────────────────────

   const renderItem = useCallback(
      ({ item, isReady, itemProps }) => (
         <MessageBubble {...itemProps} message={item} activeChatJid={activeChatJid} isReady={isReady} />
      ),
      [activeChatJid],
   );

   // ─── Render ─────────────────────────────────────────────────────────────

   return (
      <div className="conversation-list-wrapper ct-flex1 ct-flex ct-fcol ct-relative">
         <StickyChatLabel inverted scrollRef={scrollRef} messages={messages} />

         {loadingTop && <LoaderWrapper className="ct-absolute ct-top-0 ct-wFull" />}

         <SmartListView
            id="conversation-screen"
            ref={scrollRef}
            inverted
            data={messages}
            renderItem={renderItem}
            keyExtractor={item => item.msgId}
            itemHeight={80}
            overscan={20}
            offset={5}
            onLoadTop={loadNewerMessages}
            hasMoreTop={hasMoreNewer}
            onLoadBottom={loadOlderMessages}
            hasMoreBottom={hasMoreOlder}
            getItemHeight={getItemHeight}
            initialScrollKey={initialScrollKey}
            initialScrollBlock={0.1}
            enableAutoLoadMore={messages.length > 0}
            loading={loadingTop || loadingBottom}
            LoadingBottomComponent={
               loadingBottom && messages.length > 0 ? (
                  <LoaderWrapper className="ct-absolute ct-bottom-0 ct-wFull" />
               ) : null
            }
         />

         <ScrollToBottomButton scrollRef={scrollRef} activeChatJid={activeChatJid} messages={messages} />
      </div>
   );
}

export default ConversationList;
